import { useEffect, useMemo, useCallback } from 'react';
import moment from 'moment';

import useLocalStorage from 'hooks/useLocalStorage';
import useQueryParam from 'hooks/useQueryParam';

const PERIOD_ORDER = ['daily', 'weekly', 'monthly', 'yearly'];

const normalizePeriod = (period, exclude = []) => {
  const allowed = PERIOD_ORDER.filter(p => !exclude.includes(p));
  if (allowed.includes(period)) return period;
  const idx = PERIOD_ORDER.indexOf(period);
  if (idx === -1) return allowed[0] || 'weekly';

  for (let step = 1; step < PERIOD_ORDER.length; step += 1) {
    const forward = PERIOD_ORDER[idx + step];
    if (forward && allowed.includes(forward)) return forward;
    const backward = PERIOD_ORDER[idx - step];
    if (backward && allowed.includes(backward)) return backward;
  }
  return allowed[0] || 'weekly';
};

const usePersistentPeriod = (opts = {}) => {
  const { exclude = [] } = opts;

  const [storedPeriod, setStoredPeriod] = useLocalStorage('globalPeriod', 'daily');
  const [storedDate, setStoredDate] = useLocalStorage('globalDate', '');
  const [queryPeriod, setQueryPeriod] = useQueryParam('period', '');

  const currentPeriod = storedPeriod || queryPeriod || 'daily';
  const effective = useMemo(() => normalizePeriod(currentPeriod, exclude), [currentPeriod, exclude]);

  useEffect(() => {
    const normalized = normalizePeriod(currentPeriod, exclude);
    
    if (normalized !== queryPeriod) {
      setQueryPeriod(normalized);
    }
    
    if (normalized !== storedPeriod) {
      setStoredPeriod(normalized);
    }
  }, [currentPeriod, exclude, queryPeriod, setQueryPeriod, setStoredPeriod, storedPeriod]);

  const setPeriod = useCallback((newPeriod) => {
    const normalized = normalizePeriod(newPeriod, exclude);
    setStoredPeriod(normalized);
    setQueryPeriod(normalized);
  }, [exclude, setQueryPeriod, setStoredPeriod]);

  const getStoredDate = useCallback(() => {
    if (storedDate) {
      try {
        return moment(storedDate, 'YYYY-MM-DD');
      } catch (e) {
        return null;
      }
    }
    return null;
  }, [storedDate]);

  const setStoredDateValue = useCallback((date) => {
    if (date && moment.isMoment(date)) {
      setStoredDate(date.format('YYYY-MM-DD'));
    } else {
      setStoredDate('');
    }
  }, [setStoredDate]);

  return [effective, setPeriod, getStoredDate, setStoredDateValue];
};

export default usePersistentPeriod;